/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react'

import {
  DndContext,
  DragEndEvent,
  closestCenter,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensors,
  useSensor,
  KeyboardSensor,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import {
  CellContext,
  ColumnOrderState,
  ColumnSizingInfoState,
  ColumnSizingState,
  ColumnSort,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual'

import { useApi, useAuth, useCompany } from '@/hooks'
import { Company } from '@/types/dashboard-types'
import logger from '@/utils/logger'

import AssetCompositeCell from './AssetCompositeCell'
import { formatCell, getColOffset, getColumnWidth } from './assetTableCellHelper'
import SortableHeader from './SortableHeader'
import { sortAssets } from './sortingHandler'
import { getStickyBorderClass, isDraggable, stickyColumns } from './utils'

import { useMapContext } from '../MapContext'
import { useStatusColumnsContext } from '../StatusColumns'
import { AssetStateSummary, StatusColumn } from '../types'

const ASSET_COL_WIDTH = 320

interface AssetTableProps {
  assets: AssetStateSummary[]
  currentAssetId?: number
  onAssetClick: (asset: AssetStateSummary) => void
  columns: StatusColumn[]
  companyId?: number
  onFourthColRightMeasured?: (rightCoordinate: number) => void
  /** The header row's viewport position and the table's right edge, so the detail sheet can line up with them. */
  onHeaderMeasured?: (rect: { top: number; height: number; right: number }) => void
}

const initialColumnSizingInfo: ColumnSizingInfoState = {
  startOffset: null,
  startSize: null,
  deltaOffset: null,
  deltaPercentage: null,
  isResizingColumn: false,
  columnSizingStart: [],
}
interface TableBodyProps {
  table: Table<AssetStateSummary>
  virtualizer: Virtualizer<HTMLDivElement, Element>
  rows: Row<AssetStateSummary>[]
  currentAssetId?: number
  onAssetClick: (asset: AssetStateSummary) => void
  lastColumnId: string
  fillSpace: number
}

const TableBody = ({ virtualizer, rows, currentAssetId, onAssetClick, lastColumnId, fillSpace }: TableBodyProps) => (
  <tbody
    className="w-full"
    style={{
      height: `${virtualizer.getTotalSize()}px`,
      position: 'relative',
    }}
  >
    {virtualizer.getVirtualItems().map((v) => {
      const row = rows[v.index]
      const zebraBg = v.index % 2 === 0 ? 'bg-base-100' : 'bg-base-200'
      const isCurrent = currentAssetId === row.original.id
      return (
        <tr
          key={row.id}
          className="z-10 absolute w-full cursor-pointer group hover:bg-gray-50"
          style={{
            transform: `translateY(${v.start}px)`,
            height: `${v.size}px`,
          }}
          onClick={() => onAssetClick(row.original)}
        >
          {row.getVisibleCells().map((cell) => {
            const isLast = cell.column.id === lastColumnId
            const size = cell.column.getSize() + (isLast ? fillSpace : 0)
            const isAssetCol = cell.column.id === 'asset'

            return (
              <td
                key={cell.id}
                style={{
                  width: `${size}px`,
                  maxWidth: `${size}px`,
                  height: `${v.size}px`,
                  left: getColOffset(cell.column.id),
                }}
                className={`${!isDraggable(cell.column.id) ? 'relative z-10 ' + getStickyBorderClass(cell.column.id) : 'relative z-10 border-r border-gray-300'} p-0 ${isCurrent ? 'bg-base-300' : zebraBg} ${isCurrent && isAssetCol ? 'font-medium' : ''} group-hover:bg-gray-200 h-full after:content-[""] after:absolute after:left-0 after:right-0 after:top-0 after:h-px after:bg-gray-200 after:z-[100] after:pointer-events-none`}
              >
                {isAssetCol ? (
                  <div className={`h-full w-full ${isLast ? 'pr-3' : ''}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ) : (
                  <div className={`flex h-full items-center px-3 ${isLast ? 'pr-5' : ''}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                )}
              </td>
            )
          })}
        </tr>
      )
    })}
  </tbody>
)

const AssetTable = ({
  assets,
  currentAssetId,
  onAssetClick,
  columns,
  companyId,
  onFourthColRightMeasured,
  onHeaderMeasured,
}: AssetTableProps) => {
  const { api } = useApi()
  const { user } = useAuth()
  const { companies } = useCompany()
  const {
    setStatusColumns,
    assetColWidth: storedAssetWidth,
    setAssetColWidth: setStoredAssetWidth,
  } = useStatusColumnsContext()
  const { selectedSavedMap, assetDetailSheetIsOpen, sidebarState } = useMapContext()
  // While the detail sheet sits beside the list (any layout but split), the list narrows to
  // the asset column (name, alert, power and gateway), so the sheet starts right after it and
  // the list's scrollbar isn't hidden underneath the sheet.
  const assetColumnOnly = assetDetailSheetIsOpen && sidebarState.layoutMode !== 'split'

  const containerRef = useRef<HTMLDivElement>(null)
  const theadRef = useRef<HTMLTableSectionElement>(null)
  const [sorting, setSorting] = useState<ColumnSort[]>([])
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>()
  const [containerWidth, setContainerWidth] = useState(0)
  // The vertical scrollbar's width, 0 while the list fits without one.
  const [scrollbarWidth, setScrollbarWidth] = useState(0)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>(initialColumnSizingInfo)
  const prevResizingIdRef = useRef<string | false>(false)
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  )

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      setContainerWidth(el.clientWidth)
      setScrollbarWidth(el.offsetWidth - el.clientWidth)
    }
    measure()
    // Observes the content box, which narrows when the scrollbar appears, so this catches
    // the list growing past the panel's height as well as the panel resizing.
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const log = logger({ category: 'StatusColumnSelector' })

  const displayColumns = useMemo(() => {
    const dCols = columns.filter((c) => c.selected === 1)
    dCols.sort((a, b) => a.displayOrder - b.displayOrder)
    return dCols
  }, [columns])

  useEffect(() => {
    const seed: Record<string, number> = {}
    displayColumns.forEach((c) => {
      if (c.width != null) seed[c.id] = c.width
    })
    if (storedAssetWidth != null) seed['asset'] = storedAssetWidth
    setColumnWidths(seed)
  }, [displayColumns, storedAssetWidth])

  useEffect(() => {
    const sCompany = companies.find((c) => c.id === companyId)
    setSelectedCompany(sCompany)
  }, [companies, companyId])

  useEffect(() => {
    // hard coded name column at first.
    setColumnOrder([...stickyColumns, ...displayColumns.map((c) => c.id)])
  }, [displayColumns])

  const naturalWidths = useMemo<Record<string, number>>(() => {
    const widths: Record<string, number> = { asset: ASSET_COL_WIDTH }
    displayColumns.forEach((c) => {
      widths[c.id] = getColumnWidth(c.selectedColumnsDatabaseName, c.selectedColumnsDisplayName)
    })
    return widths
  }, [displayColumns])

  // Columns use their fixed default widths (no auto-grow/shrink to fill the panel). When the
  // columns are narrower than the panel the last column absorbs the slack (see fillSpace); when
  // they're wider the table scrolls horizontally. User-resized widths are layered on top below.
  const baselineSizing = useMemo<ColumnSizingState>(() => ({ ...naturalWidths }), [naturalWidths])

  const renderedSizing = useMemo<ColumnSizingState>(() => {
    const sizing: ColumnSizingState = { ...baselineSizing }
    Object.entries(columnWidths).forEach(([id, w]) => {
      if (sizing[id] !== undefined) sizing[id] = w
    })
    return sizing
  }, [baselineSizing, columnWidths])

  const handleColumnSizingChange = useCallback<OnChangeFn<ColumnSizingState>>(
    (updater) => {
      const next = typeof updater === 'function' ? updater(renderedSizing) : updater
      const changedIds = Object.keys(next).filter((id) => next[id] !== renderedSizing[id])
      if (changedIds.length === 0) return
      setColumnWidths((prev) => {
        const merged = { ...prev }
        changedIds.forEach((id) => {
          merged[id] = next[id]
        })
        return merged
      })
    },
    [renderedSizing, setColumnWidths]
  )

  const tableCols = useMemo(() => {
    return [
      {
        id: 'asset',
        accessorKey: 'name',
        header: 'Asset',
        size: ASSET_COL_WIDTH,
        minSize: 200,
        cell: ({ row }: CellContext<AssetStateSummary, unknown>) => {
          return <AssetCompositeCell asset={row.original} user={user} company={selectedCompany} />
        },
      },
      ...displayColumns.map((c) => ({
        id: c.id,
        accessorKey: c.selectedColumnsDatabaseName,
        header: c.selectedColumnsDisplayName,
        cell: ({ getValue, row }: CellContext<AssetStateSummary, unknown>) => {
          return formatCell(
            c.selectedColumnsDatabaseName,
            getValue() as string | number | null,
            row.original,
            user,
            selectedCompany
          )
        },
        size: getColumnWidth(c.selectedColumnsDatabaseName, c.selectedColumnsDisplayName),
      })),
    ]
  }, [user, displayColumns, selectedCompany, assetColumnOnly])

  const sortedData = useMemo(() => {
    return sortAssets(assets, sorting, selectedCompany)
  }, [sorting, assets, selectedCompany])

  const table = useReactTable({
    data: sortedData,
    defaultColumn: {
      size: 220,
      minSize: 50,
    },
    columns: tableCols,
    state: {
      sorting,
      columnOrder,
      columnSizing: renderedSizing,
      columnSizingInfo,
      columnVisibility: assetColumnOnly ? Object.fromEntries(displayColumns.map((c) => [c.id, false] as const)) : {},
    },
    onSortingChange: setSorting as OnChangeFn<SortingState>,
    onColumnOrderChange: setColumnOrder as OnChangeFn<ColumnOrderState>,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnSizingInfoChange: setColumnSizingInfo,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    sortDescFirst: true,
  })

  const rows = table.getRowModel().rows

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders()
    const colSizes: { [key: string]: number } = {}

    for (const header of headers) {
      colSizes[`--header-${header.id}-size`] = header.getSize()
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize()
      colSizes[`--col-${header.column.id}-fill`] = 0
    }

    const lastColumnId = columnOrder[columnOrder.length - 1]
    const totalSize = table.getTotalSize()
    const fillSpace = Math.max(0, containerWidth - totalSize)

    if (lastColumnId && fillSpace > 0) {
      colSizes[`--col-${lastColumnId}-fill`] = fillSpace
    }
    return colSizes
  }, [table.getState().columnSizingInfo, table.getState().columnSizing])
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 35,
    overscan: 10,
  })

  useEffect(() => {
    const currentId = columnSizingInfo.isResizingColumn
    const prevId = prevResizingIdRef.current
    prevResizingIdRef.current = currentId

    if (!prevId || currentId) return
    const width = columnWidths[prevId]
    if (width == null) return
    if (prevId === 'asset') {
      setStoredAssetWidth(width)
      return
    }
    const col = displayColumns.find((c) => c.id === prevId)
    if (!col) return

    const name = col.selectedColumnsDatabaseName
    setStatusColumns((prev) => prev.map((c) => (c.selectedColumnsDatabaseName === name ? { ...c, width } : c)))

    // In a saved view, the change is in-memory only and persisted when the user hits Save.
    if (selectedSavedMap) return
    api
      .put({
        path: `assets/status-columns/width`,
        origin: 'AssetStatusTable',
        body: { widths: [{ name, width }] },
      })
      .catch((err: unknown) => log.error(err))
  }, [
    columnSizingInfo.isResizingColumn,
    columnWidths,
    displayColumns,
    api,
    log,
    setStatusColumns,
    setStoredAssetWidth,
    selectedSavedMap,
  ])

  const handleDragStart = (e: DragStartEvent) => {
    setActiveColumnId(e.active.id as string)
  }
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveColumnId(null)
    const activeId = active.id as string
    const overId = over?.id as string | null
    if (!overId || activeId === overId || !isDraggable(activeId) || !isDraggable(overId)) {
      return
    }
    const old = columnOrder
    const oldIndex = old.indexOf(activeId)
    const newIndex = old.indexOf(overId)
    const newOrder = [...old]
    newOrder.splice(oldIndex, 1)
    newOrder.splice(newIndex, 0, activeId)
    setColumnOrder(newOrder)

    const start = Math.min(oldIndex, newIndex)
    const end = Math.max(oldIndex, newIndex)
    const changedColumns = newOrder.slice(start, end + 1)

    const changedDisplayOrder = changedColumns.map((col) => {
      const column = columns.find((c) => c.id === col)
      return {
        name: column?.selectedColumnsDatabaseName,
        displayOrder: newOrder.indexOf(col),
      }
    })

    setStatusColumns((prev) =>
      prev.map((c) => {
        const changed = changedDisplayOrder.find((d) => d.name === c.selectedColumnsDatabaseName)
        return changed ? { ...c, displayOrder: changed.displayOrder } : c
      })
    )

    // In a saved view, the reorder is in-memory only and persisted when the user hits Save.
    if (selectedSavedMap) return
    try {
      await api.put({
        path: `assets/status-columns/display-order`,
        origin: 'AssetStatusTable',
        body: {
          order: changedDisplayOrder,
        },
      })
    } catch (err) {
      log.error(err)
    }
  }

  const lastColumnId = columnOrder[columnOrder.length - 1] ?? ''
  const fillSpace = assetColumnOnly ? 0 : Math.max(0, containerWidth - table.getTotalSize())
  const assetColSize = table.getColumn('asset')?.getSize() ?? 0
  useLayoutEffect(() => {
    if (!onFourthColRightMeasured) return
    onFourthColRightMeasured(assetColSize)
  }, [assetColSize, onFourthColRightMeasured])

  // The scroll container resizes whenever anything above it (the panel toolbar wrapping, an
  // error banner) moves the header, so observing it and the header catches every shift.
  useLayoutEffect(() => {
    const thead = theadRef.current
    const container = containerRef.current
    if (!onHeaderMeasured || !thead || !container) return
    const measure = () => {
      const rect = thead.getBoundingClientRect()
      onHeaderMeasured({ top: rect.top, height: rect.height, right: container.getBoundingClientRect().right })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(thead)
    observer.observe(container)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [onHeaderMeasured])

  // Selecting an asset elsewhere (a map marker) brings its row into view; 'auto' leaves a
  // row that's already in view alone. Keyed on the selection only: re-running on every data
  // refresh would yank the list back after the user scrolled away.
  useEffect(() => {
    if (!currentAssetId) return
    const index = rows.findIndex((r) => r.original.id === currentAssetId)
    if (index !== -1) {
      virtualizer.scrollToIndex(index, { align: 'auto' })
    }
  }, [currentAssetId])
  return (
    <div className="flex flex-row w-full flex-1 min-h-0 min-w-0">
      <div className={`h-full min-w-0 ${assetColumnOnly ? 'w-fit overflow-x-hidden' : 'w-full overflow-x-auto'}`}>
        {/* A styled scrollbar is always drawn (macOS otherwise hides it until you scroll), so
            the list is visibly scrollable without a wheel or trackpad. Narrowed to the asset
            column, the list only scrolls vertically and is sized to the column plus whatever
            scrollbar it has: left to fit its content, the scrollbar would take its width from
            the table and add a few pixels of horizontal scroll. */}
        <div
          ref={containerRef}
          className={`h-full min-w-0 [scrollbar-width:thin] ${
            assetColumnOnly ? 'overflow-y-auto overflow-x-hidden' : 'w-full overflow-auto'
          }`}
          style={assetColumnOnly ? { width: table.getTotalSize() + scrollbarWidth } : undefined}
        >
          <DndContext
            sensors={sensors}
            modifiers={[restrictToHorizontalAxis]}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <DragOverlay>
              {activeColumnId ? (
                <div
                  className="bg-primary text-primary-content px-3 py-2 rounded shadow-2xl text-sm
  font-medium border border-primary-content/20"
                >
                  {table.getColumn(activeColumnId)?.columnDef.header as string}
                </div>
              ) : null}
            </DragOverlay>
            <table
              className="table table-sm min-w-max [&_tr]:border-b-0 [&_td]:border-b-0"
              style={{
                ...columnSizeVars,
                tableLayout: 'fixed',
                width: assetColumnOnly ? table.getTotalSize() : Math.max(table.getTotalSize(), containerWidth),
              }}
            >
              <thead ref={theadRef} className="sticky top-0 bg-primary text-primary-content z-20">
                {table.getHeaderGroups().map((hg) => (
                  <SortableContext key={hg.id} items={columnOrder.slice(1)} strategy={horizontalListSortingStrategy}>
                    <tr className="group">
                      {hg.headers.map((h) => (
                        <SortableHeader
                          key={h.id}
                          header={h}
                          sorting={sorting}
                          setSorting={setSorting}
                          lastColumnId={lastColumnId}
                          fillSpace={fillSpace}
                        />
                      ))}
                    </tr>
                  </SortableContext>
                ))}
              </thead>
              <TableBody
                table={table}
                virtualizer={virtualizer}
                rows={rows}
                currentAssetId={currentAssetId}
                onAssetClick={onAssetClick}
                lastColumnId={lastColumnId}
                fillSpace={fillSpace}
              />
            </table>
          </DndContext>
        </div>
      </div>
    </div>
  )
}

export default AssetTable
